/* eslint-disable no-restricted-globals */
import dayjs from 'dayjs';

import toast from 'atoms/toast';

import { hoursOptions } from './constants';

export function toHoursAndMinutes(totalMinutes) {
  if (!totalMinutes || isNaN(totalMinutes)) return totalMinutes;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  const result = `${hours > 0 ? `${hours} hr` : ''} ${
    minutes > 0 ? `${minutes} min` : ''
  }`;
  return result;
}

/* Checks if object is empty */
export const isEmpty = (obj) =>
  obj &&
  Object.keys(obj).length === 0 &&
  Object.getPrototypeOf(obj) === Object.prototype;

/* Checks if object is empty */
export const isNotEmptyArray = (arr) =>
  arr && Array.isArray(arr) && arr.length > 0;

/* Converts int to use comma (ex; 5000 to 5,000) */
export const parseMoneyInt = (int) => {
  if (!int) return '';
  return int.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const prettifyNumber = (num) => num.replace(/\D/g, '');

/* Formats website with correct url scheme */
export const formatWebsite = (website) => {
  const pattern = /^((http|https|ftp):\/\/)/;

  if (!website) {
    return '';
  }

  if (!pattern.test(website)) {
    return `https://${website}`;
  }
  return website;
};

export const capitalizeFirstLetter = (string) => {
  if (typeof string !== 'string') return string;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/* Clean data and format to hh:mm:ss. */
export const prettifyHoursData = (hours) => {
  const formattedHours = [];
  Object.entries(hours).forEach(([id, data]) => {
    if (
      isEmpty(data) ||
      !data.timeEnd ||
      !data.timeStart ||
      data.timeEnd.valueFormatted === 'closed' ||
      data.timeStart.valueFormatted === 'closed'
    ) {
      return;
    }
    formattedHours.push({
      ...data,
      timeEnd: data.timeEnd?.valueFormatted,
      timeStart: data.timeStart?.valueFormatted,
    });
  });
  return formattedHours;
};

export const getStatusId = (status) => {
  if (status === 'pending') return '1';
  if (status === 'approved') return '2';
  return '1,2';
};

export const prettifyHour = (str, getTime) => {
  if (!str || typeof str !== 'string') return '';
  let value = str.replace('Z', '');
  if (getTime) {
    value = getTimeFromDate(str);
  }
  // Check correct time format and split into components
  str = value.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    str,
  ];
  if (str.length > 1) {
    // If str format correct
    str = str.slice(1); // Remove full string match value
    str[5] = +str[0] < 12 ? ' AM' : ' PM'; // Set AM/PM
    str[0] = +str[0] % 12 || 12; // Adjust hours
    str[3] = null;
  }
  return str.join(''); // return adjusted time or original string
};

export const getTimeFromDate = (str) => {
  if (!str) return '';
  const [date, hour] = str.split('T');
  return hour.split('.')[0];
};

export const prettifyDate = (str) => {
  if (!str) return str;
  return `${dayjs(str).format('MMM DD, YYYY')} • ${prettifyHour(
    getTimeFromDate(str)
  )}`;
};

export const getRequestStatusLabel = (status) => {
  if (status === 'completed') return 'Paid';
  return capitalizeFirstLetter(status);
};

export const appointmentsSuccessAlert = (status) => {
  if (!status) return;
  let text;
  if (status === 'no-show') text = 'Appointment marked as no show';
  if (status === 'cancel') text = 'Appointment cancelled!';
  if (status === 'decline') text = 'Appointment declined!';
  toast({ text });
};

export const prettifyPhoneNumber = ({ number, countryCode }) => {
  if (+countryCode !== 1) return `+${countryCode} ${number}`;
  const first3Digits = number.split('').splice(0, 3).join('');
  const secont3Digits = number.split('').splice(3, 3).join('');
  const rest = number.split('').splice(6).join('');
  return `+${countryCode} (${first3Digits}) ${secont3Digits}-${rest}`;
};

export const generateKey = () => Math.random() * 100000;

// Converts from {end: '17:00:00', start: '09:00:00'} to an incremental array
// of { value: '5:00 am', valueFormatted: '05:00:00' }
export function getOpenHours(timeObj, increment = 15) {
  if (!timeObj) return [];

  const getMinutes = (time) =>
    parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
  const formatTime = (minutes) => {
    const hh = Math.floor(minutes / 60);
    const mm = minutes % 60;
    const hh12 = hh % 12 === 0 ? 12 : hh % 12;
    const period = hh >= 12 ? 'pm' : 'am';
    return {
      value: `${hh12}:${mm.toString().padStart(2, '0')} ${period}`,
      valueFormatted: `${hh.toString().padStart(2, '0')}:${mm
        .toString()
        .padStart(2, '0')}:00`,
    };
  };

  const start = getMinutes(timeObj.start);
  const end = getMinutes(timeObj.end);
  const result = [];

  if (!(end - start > increment)) {
    return [formatTime(start), formatTime(end)];
  }

  for (let minutes = start; minutes <= end; minutes += increment) {
    result.push(formatTime(minutes));
  }
  return result;
}

// Converts from {end: '17:00:00', start: '09:00:00'} to an incremental array
// of ['9:00am', '9:30am', ...]
export function getOpenHoursArray(timeObj, increment = 15) {
  if (!timeObj) return [];

  const getMinutes = (time) =>
    parseInt(time.split(':')[0]) * 60 + parseInt(time.split(':')[1]);
  const formatTime = (minutes) => {
    const hh = Math.floor(minutes / 60);
    const mm = minutes % 60;
    const hh12 = hh % 12 === 0 ? 12 : hh % 12;
    const period = hh >= 12 ? 'pm' : 'am';
    return `${hh12}:${mm.toString().padStart(2, '0')}${period}`;
  };

  const start = getMinutes(timeObj.start);
  const end = getMinutes(timeObj.end);
  const result = [];

  if (!(end - start > increment)) {
    return [formatTime(start), formatTime(end)];
  }

  for (let minutes = start; minutes <= end; minutes += increment) {
    result.push(formatTime(minutes));
  }
  return result;
}

export function shortWeekday(weekday) {
  const shortNames = {
    Monday: 'Mon',
    Tuesday: 'Tues',
    Wednesday: 'Wed',
    Thursday: 'Thurs',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun',
  };
  return shortNames[weekday] || weekday;
}

// We start close the dropdown +15min from the timeStart value
export const closeTimeHours = (timeStart, hoursAvailable) => {
  const hours = hoursAvailable || hoursOptions;
  const time = timeStart?.value?.toLowerCase();

  if (time === 'closed' || !time || hoursAvailable?.length === 1) return hours;

  const index = hours.findIndex((el) => el.value === time);
  const followingValueIndex = index + 1;

  return hours.slice(followingValueIndex);
};

export const serviceDetails = (service) => {
  if (!service) return '';

  const animalType = service.animal?.type?.value;
  const animalBreed = service.breed?.value;
  const animalSize = service.size?.value;
  const animalHair = service.hair?.length?.value;

  const typeString = animalType || '';
  const breedString = animalBreed ? `, ${animalBreed}` : '';
  const sizeString = animalSize ? `, ${animalSize}` : '';
  const hairString = animalHair ? `, ${animalHair}` : '';

  return `${typeString}${breedString}${sizeString}${hairString}`;
};

export const getCardIcon = (str) => {
  const cardsWithIcons = [
    'amex',
    'discover',
    'elo',
    'hiper',
    'jcb',
    'maestro',
    'mastercard',
    'paypal',
    'unionpay',
    'visa',
  ];
  if (cardsWithIcons.includes(str)) return str;
  return 'card';
};

export const getDayFromDate = (str) => {
  if (!str) return '';
  const [date] = str.split('T');
  return date;
};

export function isDateWithinLast3Days(date) {
  const givenDate = new Date(date);
  givenDate.setHours(0, 0, 0, 0); // Remove time component

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Remove time component

  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(today.getDate() - 3); // 3 days before today
  threeDaysAgo.setHours(0, 0, 0, 0);

  return givenDate >= threeDaysAgo && givenDate <= today;
}

export const isToday = (date) => dayjs().isSame(date, 'date');

export const isInThePast = (date) => dayjs().isAfter(date, 'date');

export const isPastDateTime = (date) => dayjs().isAfter(date);

export const addMinutesToDate = (dateStr, minutes) => {
  const date = new Date(dateStr);
  date.setMinutes(date.getMinutes() + minutes);
  return date.toISOString();
};

export function removeSeconds(timeString) {
  // Split the timeString by ":" and take the first two parts (hours and minutes)
  const parts = timeString.split(':');
  const hoursAndMinutes = parts.slice(0, 2).join(':');
  return hoursAndMinutes;
}

export const addDays = (date, days) => {
  if (!date) return '';
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const getServiceNameWithTags = (service) => {
  const animal = service.animal.type?.value;
  const size = service.size?.value;
  const hair = service.hair?.length?.value;
  const breed = service.breed?.value;
  const tags =
    animal || size || hair || breed
      ? `(${[animal, size, hair, breed].filter(Boolean).join(', ')})`
      : '';
  return `${service.name} ${tags}`;
};

export function isThirtyMinutesApart(date1, date2) {
  // Convert both dates to milliseconds
  const time1 = date1.getTime();
  const time2 = date2.getTime();

  // Calculate the difference in milliseconds
  const difference = Math.abs(time2 - time1);

  // Convert the difference to minutes
  const minutesDifference = difference / (1000 * 60);

  // Check if the difference is exactly 30 minutes
  return minutesDifference === 30;
}

export const openHourOptions = (date, hours, selectedStartTime) => {
  if (!date) return hoursOptions.filter((el) => el.value !== 'Closed');

  const { start, end } = getOpenAndClosingHours(date, hours);

  const filteredHoursOptions = hoursOptions.filter(
    (option) =>
      option.valueFormatted !== 'closed' &&
      option.valueFormatted >= start &&
      option.valueFormatted <= end
  );

  if (selectedStartTime) {
    return sortHoursOptions(filteredHoursOptions, selectedStartTime);
  }
  return filteredHoursOptions;
};

// DOW reference:
// DAYJS:  1 - 0 (Mon - Sun)
// API: 1 - 7 (Mon - Sun)
export const getOpenAndClosingHours = (date, hours) => {
  const dow = parseInt(dayjs(new Date(date)).utc().format('d'));
  const dowId = dow === 0 ? 7 : dow;

  const openHours = hours.filter((el) => el.dow.id === dowId)[0];

  if (!openHours) {
    return { start: null, end: null };
  }

  const { start, end } = openHours.time;
  return { start, end };
};

function sortHoursOptions(hoursOptions, startTime) {
  const startIndex = hoursOptions.findIndex(
    (option) => option.valueFormatted === startTime
  );

  if (startIndex === -1) {
    return hoursOptions;
  }

  return hoursOptions.slice(startIndex + 1);
}

export const hashData = async (data) => {
  // Convert the data object to a JSON string
  const jsonData = JSON.stringify(data);

  // Convert the JSON string to an ArrayBuffer
  const buffer = new TextEncoder().encode(jsonData);

  // Use the SubtleCrypto API to hash the ArrayBuffer
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);

  // Convert the hash (ArrayBuffer) to a hex string
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};

export function isLessThan24HoursAway(dateA, dateB) {
  // Get the time in milliseconds for both dates
  const timeA = dateA.getTime();
  const timeB = dateB.getTime();

  const difference = Math.abs(timeA - timeB);

  // Check if the difference is less than 24 hours (in milliseconds)
  return difference < 86400000;
}

export const updateLocalStorage = (key, state) => {
  localStorage.setItem(key, JSON.stringify(state));
};
